import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Image as ImageIcon } from "lucide-react";

export async function compressImage(blob, max = 1600) {
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  return new Promise((res) => c.toBlob(res, "image/jpeg", 0.85));
}

export function CameraCapture({ onCapture, testPrefix = "snap" }) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let stream;
    setReady(false);
    if (!navigator.mediaDevices?.getUserMedia) { setError("Camera unavailable — choose from gallery"); return; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false })
      .then((s) => { stream = s; videoRef.current.srcObject = s; setReady(true); setError(""); })
      .catch(() => setError("Camera unavailable — choose from gallery"));
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, [facing]);

  const shoot = () => {
    const v = videoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    if (facing === "user") { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0);
    c.toBlob(async (b) => onCapture(await compressImage(b)), "image/jpeg", 0.9);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onCapture(await compressImage(f));
  };

  return (
    <div className="relative aspect-[3/4] w-full bg-void rounded-secure overflow-hidden border border-glass" data-testid={`${testPrefix}-camera`}>
      <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${facing === "user" ? "-scale-x-100" : ""}`} />
      {error && <p className="absolute inset-0 flex items-center justify-center text-sm text-ash/50 px-6 text-center" data-testid={`${testPrefix}-camera-error`}>{error}</p>}
      <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-6 py-4 bg-gradient-to-t from-void/90 to-transparent">
        <button onClick={() => fileRef.current?.click()} data-testid={`${testPrefix}-gallery-btn`} className="p-2.5 rounded-full border border-rim bg-void/60 text-ash hover:text-stellar"><ImageIcon size={20} strokeWidth={1.5} /></button>
        <button onClick={shoot} disabled={!ready} data-testid={`${testPrefix}-shutter-btn`} className="w-16 h-16 rounded-full border-2 border-stellar flex items-center justify-center shadow-stellar-glow disabled:opacity-30">
          <span className="w-12 h-12 rounded-full bg-stellar/90" />
        </button>
        <button onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} data-testid={`${testPrefix}-flip-btn`} className="p-2.5 rounded-full border border-rim bg-void/60 text-ash hover:text-stellar"><RefreshCw size={20} strokeWidth={1.5} /></button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testPrefix}-gallery-input`} />
      {!ready && !error && <Camera size={28} strokeWidth={1.5} className="absolute inset-0 m-auto text-ash/30 animate-pulse" />}
    </div>
  );
}
